import { formatNumber, reducePrecision } from "@/utils/numbers";
import { getAcronyms } from "@/utilities";
import { useTranslation } from "react-i18next";
import React from "react";
import Text from "@/components/ui/text";
import { StageData } from "@/entities/reporting/types";
import {
  avatarColorIndex,
  salesPipelineColors
} from "@/components/layouts/SalesPipeline/SalesColors";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { isEmpty } from "lodash";

interface Props {
  active?: boolean;
  payload?: any[] | undefined;
  index: number;
  datasets: StageData[];
  userId: string | null;
  userIndex: number;
  isArr: boolean;
  isProbabilityMode: boolean;
}

const displayAcronyms = (title: string) => {
  const acronyms = getAcronyms(title);
  if (acronyms === "") {
    return "";
  }
  return acronyms.join("");
};

export const CustomTooltip: React.FC<Props> = React.memo(
  ({
    active,
    payload,
    index,
    datasets,
    userId,
    userIndex,
    isProbabilityMode
  }) => {
    const { t } = useTranslation();
    const { label: arrMrrLabel } = useArrMrr();
    if (!datasets || isEmpty(datasets)) {
      return null;
    }

    const title = React.useMemo(
      () =>
        userId ? datasets[index].contributors[userId].user?.displayName : "",
      [datasets, index, userId]
    );
    const { value, currencySign } = React.useMemo(
      () =>
        formatNumber(
          userId
            ? isProbabilityMode
              ? datasets[index].contributors[userId].weightedValue
              : datasets[index].contributors[userId].value
            : 0,
          1,
          true,
          false,
          true
        ),
      [datasets, index, userId]
    );
    if (!active || !payload || payload.length === 0) {
      return null;
    }
    const amount = `${currencySign}${value}`;
    return (
      <>
        {userId && (
          <div className="relative z-50">
            <Text
              as="div"
              data-test="pipeline-funnel-tooltip"
              variant="body"
              className="bg-surface-tooltip-toast px-3 py-1.5 shadow-md w-fit"
            >
              <Text
                as="div"
                variant="label"
                className="flex flex-col gap-6 text-start"
              >
                <div className="flex gap-1 space-x-2 items-center self-center">
                  <div
                    className="avatar min-w-6 !size-6 !text-sm flex items-center justify-center text-h2"
                    style={{
                      borderRadius: "50%",
                      backgroundColor:
                        salesPipelineColors[avatarColorIndex(userIndex)]
                          .stroke || "black"
                    }}
                  >
                    {displayAcronyms(title ?? "")}
                  </div>
                  <div>{title}</div>
                </div>
                <div className="flex flex-col gap-5 text-start">
                  <div className="flex flex-col gap-2">
                    <Text
                      as="div"
                      variant="bodySecondary"
                    >{`${datasets[index].stageName} ${t("salesPipeline.tooltipTotal")} ${arrMrrLabel}`}</Text>
                    <div>{amount}</div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Text as="div" variant="bodySecondary">
                      {t("salesPipeline.closing")}
                    </Text>
                    <div>{`${reducePrecision(Number(datasets[index].contributors[userId].avgProbability) * 100)}%`}</div>
                  </div>
                </div>
              </Text>
            </Text>
            <div className="absolute z-50 -bottom-3 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[20px] border-t-surface-tooltip-toast"></div>
          </div>
        )}
      </>
    );
  }
);
